import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authService } from '../services/authService';

/**
 * Permission levels for role-based access control
 * @enum {number}
 */
export const PERMISSION_LEVELS = {
  NONE: 0,
  VIEW: 1,
  CREATE: 2,
  EDIT: 3,
  DELETE: 4,
  FULL: 5,
};

/**
 * Available permission modules
 * @type {string[]}
 */
export const PERMISSION_MODULES = [
  'customers',
  'sites',
  'templates',
  'parts',
  'quotes',
  'projects',
  'documentation',
  'service',
  'reports',
  'admin',
];

/**
 * @typedef {Object} Role
 * @property {number} id - Role ID
 * @property {string} name - Role slug (e.g., 'super_admin', 'quotes_manager')
 * @property {string} display_name - Human-readable role name
 * @property {string} [icon] - Role icon
 * @property {string} [color] - Role color
 */

/**
 * @typedef {Object.<string, number>} EffectivePermissions
 * Module permissions where key is module name and value is permission level (0-5)
 */

/**
 * @typedef {Object} User
 * @property {number} id - User ID
 * @property {string} name - User full name
 * @property {string} email - User email address
 * @property {string} [type] - Legacy user type field
 * @property {Role[]} [roles] - Array of role objects
 * @property {string[]} [role_names] - Array of role names
 * @property {EffectivePermissions} [effective_permissions] - Module permissions
 * @property {string} [avatar] - User avatar URL
 * @property {string} [created_at] - User creation timestamp
 */

/**
 * @typedef {Object} AuthState
 * @property {import('vue').Ref<User|null>} user - Current authenticated user
 * @property {import('vue').Ref<string|null>} token - Authentication token
 * @property {import('vue').Ref<string|null>} error - Error message if any
 * @property {import('vue').Ref<boolean>} isLoading - Loading state
 * @property {import('vue').ComputedRef<boolean>} isAuthenticated - Whether user is authenticated
 * @property {import('vue').ComputedRef<boolean>} isAdmin - Whether user is admin
 * @property {import('vue').ComputedRef<boolean>} isManager - Whether user is manager or admin
 * @property {import('vue').ComputedRef<string[]>} roleNames - Current user role names
 * @property {import('vue').ComputedRef<number|undefined>} userId - Current user ID
 * @property {import('vue').ComputedRef<(module: string, level?: number) => boolean>} hasPermission - Check module permission level
 * @property {import('vue').ComputedRef<(action: string, module: string) => boolean>} canAccess - Check action permission
 * @property {import('vue').ComputedRef<(roleName: string) => boolean>} hasRole - Check if user has role
 */

/**
 * @typedef {Object} AuthMethods
 * @property {(email: string, password: string) => Promise<Object>} login - Login with credentials
 * @property {(redirectToLogin?: boolean) => void} logout - Logout and optionally redirect
 * @property {() => void} clearAuth - Clear auth state without redirect
 * @property {() => Promise<boolean>} verifyAuth - Verify authentication token
 * @property {(currentPassword: string, newPassword: string) => Promise<Object>} changePassword - Change user password
 * @property {(email: string) => Promise<Object>} requestPasswordReset - Request password reset
 * @property {(resetToken: string, newPassword: string) => Promise<Object>} resetPassword - Reset password with token
 * @property {() => Promise<Object>} getUserProfile - Get current user profile
 */

/**
 * Authentication store using standardized API client
 * @returns {AuthState & AuthMethods}
 */
export const useAuthStore = defineStore('auth', () => {
  // Initialize user from sessionStorage if available
  let initialUser = null;
  let initialToken = sessionStorage.getItem('token');

  const storedUser = sessionStorage.getItem('user');
  if (storedUser) {
    try {
      initialUser = JSON.parse(storedUser);
    } catch (e) {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
      initialUser = null;
      initialToken = null;
    }
  }

  const user = ref(initialUser);
  const token = ref(initialToken);
  const error = ref(null);
  const isLoading = ref(false);

  // Computed properties
  const isAuthenticated = computed(() => !!token.value && authService.isTokenValid(token.value));
  const userId = computed(() => user.value?.id);

  /**
   * Check if user has a specific role by name
   * @param {string} roleName - Role name to check (e.g., 'super_admin', 'quotes_manager')
   * @returns {boolean}
   */
  const hasRole = computed(() => (roleName) => {
    if (!user.value?.role_names) {
      return false;
    }
    return user.value.role_names.includes(roleName);
  });

  /**
   * Get array of user's role names
   * @returns {string[]}
   */
  const roleNames = computed(() => user.value?.role_names || []);

  /**
   * Check if user has at least the specified permission level for a module
   * @param {string} module - Module name (customers, quotes, sites, etc.)
   * @param {number} requiredLevel - Minimum permission level (default: 1 = VIEW)
   * @returns {boolean}
   */
  const hasPermission = computed(() => (module, requiredLevel = 1) => {
    if (!user.value?.effective_permissions) {
      return false;
    }
    const userLevel = user.value.effective_permissions[module] || 0;
    return userLevel >= requiredLevel;
  });

  /**
   * Check if user can perform an action on a module
   * Maps action names to permission levels
   * @param {string} action - Action name (view, create, edit, update, delete, destroy, full, manage)
   * @param {string} module - Module name
   * @returns {boolean}
   */
  const canAccess = computed(() => (action, module) => {
    const levelMap = {
      view: PERMISSION_LEVELS.VIEW,
      read: PERMISSION_LEVELS.VIEW,
      create: PERMISSION_LEVELS.CREATE,
      add: PERMISSION_LEVELS.CREATE,
      edit: PERMISSION_LEVELS.EDIT,
      update: PERMISSION_LEVELS.EDIT,
      delete: PERMISSION_LEVELS.DELETE,
      destroy: PERMISSION_LEVELS.DELETE,
      remove: PERMISSION_LEVELS.DELETE,
      full: PERMISSION_LEVELS.FULL,
      manage: PERMISSION_LEVELS.FULL,
      admin: PERMISSION_LEVELS.FULL,
    };
    const level = levelMap[action.toLowerCase()] || PERMISSION_LEVELS.VIEW;
    return hasPermission.value(module, level);
  });

  /**
   * Check if user is an admin (has super_admin role, admin role, or full admin permissions)
   * @returns {boolean}
   */
  const isAdmin = computed(() => {
    return hasRole.value('super_admin') ||
           hasRole.value('admin') ||
           hasPermission.value('admin', PERMISSION_LEVELS.FULL);
  });

  /**
   * Check if user is a manager or higher
   * Has at least EDIT level on most modules or has a manager role
   * @returns {boolean}
   */
  const isManager = computed(() => {
    if (isAdmin.value) {
      return true;
    }
    // Check for any manager-type role
    const managerRoles = ['quotes_manager', 'sites_manager', 'projects_manager', 'service_manager'];
    return managerRoles.some((role) => hasRole.value(role));
  });

  /**
   * Legacy userRole computed for backward compatibility
   * Returns first role name or 'user'
   * @deprecated Use roleNames or hasRole instead
   */
  const userRole = computed(() => {
    if (user.value?.role_names?.length > 0) {
      return user.value.role_names[0];
    }
    // Fallback to legacy type field
    return user.value?.type || 'user';
  });
  
  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Authentication result
   */
  const login = async (email, password) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Use the standardized authService
      const data = await authService.login(email, password);
      
      // Store authentication data
      token.value = data.token;
      user.value = data.user;
      sessionStorage.setItem('token', data.token);

      // Store user data in sessionStorage
      if (data.user) {
        sessionStorage.setItem('user', JSON.stringify(data.user));
      }

      return data;
    } catch (err) {
      error.value = err.message || 'Login failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Logout user and clear authentication state
   * @param {boolean} redirectToLogin - Whether to redirect to login page
   */
  const logout = (redirectToLogin = true) => {
    user.value = null;
    token.value = null;
    error.value = null;
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    
    if (redirectToLogin) {
      // Redirect to Laravel blade login page instead of Vue route
      window.location.href = '/login';
    }
  };

  /**
   * Clear authentication state without redirecting
   * Used when token is invalid or expired
   */
  const clearAuth = () => {
    user.value = null;
    token.value = null;
    error.value = null;
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  };

  /**
   * Verify current authentication token and load user data
   * @returns {Promise<boolean>} Whether verification succeeded
   */
  const verifyAuth = async () => {
    try {
      // Skip verification if no token
      if (!token.value) {
        return false;
      }
      
      isLoading.value = true;
      error.value = null;
      
      // Validate token format
      if (!authService.isTokenValid(token.value)) {
        logout(false);
        return false;
      }

      // Verify with server
      const response = await authService.verifyAuth();

      // Extract user from response (API returns { success, authenticated, user })
      const userData = response.user || response;
      user.value = userData;

      // Store user data in sessionStorage for persistence
      if (userData) {
        sessionStorage.setItem('user', JSON.stringify(userData));
      }

      return true;
    } catch (err) {
      error.value = err.message;
      logout(false);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password change result
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      isLoading.value = true;
      error.value = null;
      
      const result = await authService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} Reset request result
   */
  const requestPasswordReset = async (email) => {
    try {
      isLoading.value = true;
      error.value = null;
      
      const result = await authService.requestPasswordReset(email);
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reset password with token
   * @param {string} resetToken - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Reset result
   */
  const resetPassword = async (resetToken, newPassword) => {
    try {
      isLoading.value = true;
      error.value = null;
      
      const result = await authService.resetPassword({
        token: resetToken,
        password: newPassword,
      });
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile
   */
  const getUserProfile = async () => {
    try {
      isLoading.value = true;
      error.value = null;
      
      const profile = await authService.getUserProfile();
      user.value = profile;
      
      return profile;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    user,
    token,
    error,
    isLoading,

    // Getters - Role-based
    isAuthenticated,
    isAdmin,
    isManager,
    hasRole,
    roleNames,
    hasPermission,
    canAccess,

    // Getters - Legacy (deprecated)
    userRole,
    userId,

    // Actions
    login,
    logout,
    clearAuth,
    verifyAuth,
    changePassword,
    requestPasswordReset,
    resetPassword,
    getUserProfile,
  };
});